import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Landmark,
  FileText,
  Wallet,
  Receipt,
  BellRing,
  AlertTriangle,
  FileSignature,
  BarChart3,
  LogOut,
  RefreshCw,
  Smartphone,
  PiggyBank,
  Users,
  Activity,
  ShieldCheck,
  Menu,
  X,
  Eye,
  ArrowLeftRight,
  Tag,
  Wrench,
  Factory,
  CalendarDays,
  ShoppingCart,
  Plus,
  ClipboardList,
  QrCode,
  ChevronRight,
  Coins,
  Trash2,
  ListFilter,
  Archive,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useFx } from "@/context/FxContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import CompanySwitcher from "@/components/CompanySwitcher";

// Finance items live inside a collapsible "Finans" group.  Only admin and
// muhasebe roles ever see this group; every other role has these routes
// hidden entirely from the sidebar.
const FINANCE_ITEMS = [
  { to: "/hesaplar",          label: "Banka Hesapları",   icon: Landmark,       testid: "nav-accounts" },
  { to: "/cekler",            label: "Çekler",            icon: FileText,       testid: "nav-cheques" },
  { to: "/alacaklar",         label: "Alacaklar",         icon: Wallet,         testid: "nav-receivables" },
  { to: "/giderler",          label: "Giderler",          icon: Receipt,        testid: "nav-expenses" },
  { to: "/butceler",          label: "Bütçeler",          icon: PiggyBank,      testid: "nav-budgets" },
  { to: "/odemeler",          label: "Yaklaşan Ödemeler", icon: BellRing,       testid: "nav-upcoming" },
  { to: "/nakit-akisi",       label: "Nakit Akışı",       icon: ArrowLeftRight, testid: "nav-payments" },
  { to: "/gecikmis-odemeler", label: "Gecikmiş Ödemeler", icon: AlertTriangle,  testid: "nav-overdue" },
];
const FINANCE_PATHS = new Set(FINANCE_ITEMS.map((f) => f.to));

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/sozlesmeler", label: "Sözleşmeler", icon: FileSignature, testid: "nav-contracts" },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, testid: "nav-reports" },
  { to: "/uretim", label: "Üretim Planlama", icon: Factory, testid: "nav-production" },
  { to: "/uretim-takvimi", label: "Üretim Takvimi", icon: CalendarDays, testid: "nav-production-calendar" },
  { to: "/kademe", label: "Kademe", icon: Wrench, testid: "nav-kademe" },
  { to: "/bayi-siparisleri", label: "Bayi Siparişleri", icon: ShoppingCart, testid: "nav-bayi-orders-admin" },
  { to: "/bayi/siparis-ver", label: "Sipariş Ver", icon: Plus, testid: "nav-bayi-order-new" },
  { to: "/bayi/siparislerim", label: "Siparişlerim", icon: ClipboardList, testid: "nav-bayi-my-orders" },
  { to: "/barkod-stok", label: "Barkod ve Stok", icon: QrCode, testid: "nav-pallets" },
];

const CURRENCIES = ["TRY", "USD", "EUR"];

/**
 * Collapsible "Finans" group.  Renders the finance items indented under a
 * header row that folds the whole section away.  When the sidebar itself is
 * collapsed, the group is force-open and shows only icons so operators can
 * still click through.
 */
function FinanceGroup({ open, onToggle, collapsed, onNavigate }) {
  const showChildren = collapsed || open;
  return (
    <div className="mt-1" data-testid="finance-group">
      {!collapsed && (
        <button
          type="button"
          onClick={onToggle}
          className="nf-side-item w-full justify-between text-zinc-400 hover:text-white"
          aria-expanded={open}
          data-testid="finance-group-toggle"
        >
          <span className="flex items-center gap-2">
            <Coins size={16} strokeWidth={2} className="shrink-0" />
            <span className="whitespace-nowrap">Finans</span>
          </span>
          <ChevronRight
            size={14}
            className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          />
        </button>
      )}
      {showChildren && (
        <div className={collapsed ? "space-y-1" : "ml-2 pl-2 border-l border-zinc-800 space-y-1 mt-1"}>
          {FINANCE_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                data-testid={item.testid}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `nf-side-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`
                }
              >
                <Icon size={16} strokeWidth={2} className="shrink-0" />
                <span className={`transition-opacity duration-200 whitespace-nowrap ${collapsed ? "hidden" : "opacity-100"}`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onNavigate, onLogout, user, isAdmin, isMuhasebe, isSales, isKademe, isSantiye, isSirketMuduru, isBayi, isSevkiyat, isOperator, collapsed = false, onToggleCollapse }) {
  const handleClick = () => onNavigate && onNavigate();
  // Only admin & muhasebe get the finance group; everyone else has it hidden.
  const canSeeFinance = isAdmin || isMuhasebe;
  // Persist the collapsed/expanded state of the Finans group across reloads.
  const [financeOpen, setFinanceOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("nf.sidebar.financeOpen") !== "false";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("nf.sidebar.financeOpen", String(financeOpen));
    }
  }, [financeOpen]);

  // Role-based nav visibility.  Each restricted role sees ONLY its own tabs.
  let visibleNav = NAV;
  if (isBayi) {
    visibleNav = NAV.filter((n) => ["/bayi/siparis-ver", "/bayi/siparislerim"].includes(n.to));
  } else if (isSevkiyat) {
    // Shipping: existing stock workflows + read-only Production Calendar.
    visibleNav = NAV.filter((n) => ["/barkod-stok", "/uretim-takvimi"].includes(n.to));
  } else if (isOperator) {
    // Operatör: sadece Barkod & Stok (Palet Oluştur + Etiket Basım).
    visibleNav = NAV.filter((n) => n.to === "/barkod-stok");
  } else if (isSales) {
    // Satış: existing workflows plus read-only pallet reports.
    visibleNav = NAV.filter((n) => ["/sozlesmeler", "/uretim", "/uretim-takvimi", "/barkod-stok"].includes(n.to));
  } else if (isKademe) {
    visibleNav = NAV.filter((n) => n.to === "/kademe");
  } else if (isSantiye) {
    // Şantiye Müdürü: mevcut üretim/kademe/bayi hakları + palet işlemleri.
    visibleNav = NAV.filter((n) => ["/uretim", "/uretim-takvimi", "/kademe", "/bayi-siparisleri", "/barkod-stok"].includes(n.to));
  } else if (isSirketMuduru) {
    // Şirket Müdürü: Sözleşmeler (RW) + Teklifler (RO, onay yok) + Üretim
    // Planlama/Takvim (RO) + Kademe (RW) + Bayi Siparişleri (onaylayıcı)
    // Note: /cekler moved into the finance group which is admin/muhasebe-only.
    visibleNav = NAV.filter((n) => ["/sozlesmeler", "/teklifler", "/uretim", "/uretim-takvimi", "/kademe", "/bayi-siparisleri", "/barkod-stok"].includes(n.to));
  } else if (isAdmin) {
    // Admin sees everything EXCEPT the dealer-only tabs (they aren't a dealer).
    visibleNav = NAV.filter((n) => !["/bayi/siparis-ver", "/bayi/siparislerim"].includes(n.to));
  } else {
    // Default (muhasebe/viewer) — hide all bayi-specific tabs and the
    // admin-only Barkod & Stok module.
    visibleNav = NAV.filter((n) => !n.to.startsWith("/bayi") && n.to !== "/barkod-stok");
  }
  return (
    <>
      <div className={`border-b border-zinc-900 ${collapsed ? "px-2 py-4" : "px-4 py-6"}`}>
        <div className={`flex items-center gap-3 ${collapsed ? "flex-col" : ""}`}>
          {/* Hamburger toggle — desktop only. Mobile drawer uses the header hamburger. */}
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden lg:inline-flex items-center justify-center w-9 h-9 rounded-md text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors shrink-0"
              data-testid="sidebar-toggle-btn"
              aria-label={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
              title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
            >
              <Menu size={20} strokeWidth={2.2} />
            </button>
          )}
          <img
            src="/aladag-emblem-transparent.png"
            alt="Aladag Finans"
            className={`object-contain shrink-0 transition-all duration-300 ${collapsed ? "hidden" : "w-11 h-8"}`}
            data-testid="brand-logo"
          />
          <div
            className={`overflow-hidden transition-all duration-300 ${
              collapsed ? "opacity-0 w-0 max-w-0" : "opacity-100 w-auto max-w-[140px]"
            }`}
          >
            <div className="nf-heading text-white font-bold text-lg leading-tight whitespace-nowrap">Aladag Finans</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest whitespace-nowrap">
              {isSales ? "Sözleşme + Üretim"
                : isKademe ? "Kademe Modülü"
                : isSantiye ? "Şantiye Müdürü"
                : isSirketMuduru ? "Şirket Müdürü"
                : isBayi ? "Bayi Portalı"
                : isSevkiyat ? "Sevkiyat"
                : isOperator ? "Operatör"
                : "Nakit Akışı"}
            </div>
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="px-3 pt-4">
          <CompanySwitcher />
        </div>
      )}

      <nav className={`flex-1 py-4 space-y-1 mt-2 overflow-y-auto overflow-x-hidden ${collapsed ? "px-2" : "px-3"}`}>
        {visibleNav.map((item, idx) => {
          const Icon = item.icon;
          const link = (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={handleClick}
              data-testid={item.testid}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) => `nf-side-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`}
            >
              <Icon size={16} strokeWidth={2} className="shrink-0" />
              <span className={`transition-opacity duration-200 whitespace-nowrap ${collapsed ? "hidden" : "opacity-100"}`}>
                {item.label}
              </span>
            </NavLink>
          );
          // Inject the "Finans" collapsible group right after Genel Bakış so
          // it sits at the top of the finance operator's view.
          if (item.to === "/" && canSeeFinance) {
            return (
              <div key="__with-finance__" className="contents">
                {link}
                <FinanceGroup
                  open={financeOpen}
                  onToggle={() => setFinanceOpen((v) => !v)}
                  collapsed={collapsed}
                  onNavigate={handleClick}
                />
              </div>
            );
          }
          return link;
        })}
        {!isSales && !isKademe && !isSantiye && !isSirketMuduru && !isBayi && !isSevkiyat && !isOperator && (
          <div className={`pt-3 mt-3 border-t border-zinc-900 space-y-1`}>
            <NavLink
              to="/ayarlar/kategoriler"
              onClick={handleClick}
              className={`nf-side-item ${collapsed ? "justify-center px-2" : ""}`}
              title={collapsed ? "Kategoriler" : undefined}
              data-testid="nav-categories"
            >
              <Tag size={16} className="shrink-0" />
              <span className={collapsed ? "hidden" : ""}>Kategoriler</span>
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/kullanicilar"
                onClick={handleClick}
                className={`nf-side-item ${collapsed ? "justify-center px-2" : ""}`}
                title={collapsed ? "Kullanıcı Yönetimi" : undefined}
                data-testid="nav-users"
              >
                <Users size={16} className="shrink-0" />
                <span className={collapsed ? "hidden" : ""}>Kullanıcı Yönetimi</span>
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/admin/teklif-silme-talepleri"
                onClick={handleClick}
                className={`nf-side-item ${collapsed ? "justify-center px-2" : ""}`}
                title={collapsed ? "Teklif Silme Talepleri" : undefined}
                data-testid="nav-teklif-delete-requests"
              >
                <Trash2 size={16} className="shrink-0" />
                <span className={collapsed ? "hidden" : ""}>Teklif Silme Talepleri</span>
              </NavLink>
            )}
            {isAdmin && (
              <NavLink to="/admin/uretim-disi-kalemler" onClick={handleClick}
                className={({ isActive }) => `nf-side-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`}
                title={collapsed ? 'Üretim Dışı Kalemler' : undefined} data-testid="nav-production-exclusions">
                <ListFilter size={16} className="shrink-0" />
                <span className={collapsed ? 'hidden' : ''}>Üretim Dışı Kalemler</span>
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/etkinlik-gunlugu"
                onClick={handleClick}
                className={`nf-side-item ${collapsed ? "justify-center px-2" : ""}`}
                title={collapsed ? "Etkinlik Günlüğü" : undefined}
                data-testid="nav-audit"
              >
                <Activity size={16} className="shrink-0" />
                <span className={collapsed ? "hidden" : ""}>Etkinlik Günlüğü</span>
              </NavLink>
            )}
            {isAdmin && (
              <NavLink to="/arsiv-temizligi" onClick={handleClick}
                className={`nf-side-item ${collapsed ? "justify-center px-2" : ""}`}
                title={collapsed ? "Arşiv Temizliği" : undefined} data-testid="nav-archive-cleanup">
                <Archive size={16} className="shrink-0" />
                <span className={collapsed ? "hidden" : ""}>Arşiv Temizliği</span>
              </NavLink>
            )}
            <NavLink
              to="/mobil"
              onClick={handleClick}
              className={`nf-side-item ${collapsed ? "justify-center px-2" : ""}`}
              title={collapsed ? "Mobil Denetim" : undefined}
              data-testid="nav-mobile"
            >
              <Smartphone size={16} className="shrink-0" />
              <span className={collapsed ? "hidden" : ""}>Mobil Denetim</span>
            </NavLink>
          </div>
        )}
      </nav>

      <div className={`border-t border-zinc-900 ${collapsed ? "p-2" : "p-4"}`}>
        {collapsed ? (
          <button
            onClick={onLogout}
            className="w-full inline-flex items-center justify-center h-10 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900"
            data-testid="logout-btn"
            title={`Çıkış Yap — ${user?.name || ""}`}
          >
            <LogOut size={16} />
          </button>
        ) : (
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="text-xs text-zinc-500 uppercase tracking-widest">Kullanıcı</div>
              {(() => {
                const meta = {
                  admin: { cls: "bg-emerald-500/20 text-emerald-300", Icon: ShieldCheck, label: "Yönetici" },
                  viewer: { cls: "bg-sky-500/20 text-sky-300", Icon: Eye, label: "Görüntüleyici" },
                  satis: { cls: "bg-rose-500/20 text-rose-300", Icon: null, label: "Satış Personeli" },
                  kademe: { cls: "bg-amber-500/20 text-amber-300", Icon: Wrench, label: "Kademe" },
                  santiye: { cls: "bg-orange-500/20 text-orange-300", Icon: null, label: "Şantiye Müdürü" },
                  sirket_muduru: { cls: "bg-purple-500/20 text-purple-300", Icon: null, label: "Şirket Müdürü" },
                  bayi: { cls: "bg-teal-500/20 text-teal-300", Icon: ShoppingCart, label: "Bayi" },
                  sevkiyat: { cls: "bg-indigo-500/20 text-indigo-300", Icon: null, label: "Sevkiyat" },
                  operator: { cls: "bg-cyan-500/20 text-cyan-300", Icon: null, label: "Operatör" },
                  muhasebe: { cls: "bg-blue-500/20 text-blue-300", Icon: null, label: "Muhasebe" },
                };
                const m = meta[user?.role] || meta.muhasebe;
                const Icon = m.Icon;
                return (
                  <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded ${m.cls} text-[9px] uppercase tracking-widest font-bold`} data-testid="role-badge">
                    {Icon && <Icon size={9} />} <span>{m.label}</span>
                  </span>
                );
              })()}
            </div>
            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">
              {user?.name || "—"}
            </div>
            <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            className="p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900"
            data-testid="logout-btn"
            title="Çıkış Yap"
          >
            <LogOut size={16} />
          </button>
        </div>
        )}
      </div>
    </>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, isAdmin, isMuhasebe, isSales, isKademe, isSantiye, isSirketMuduru, isBayi, isSevkiyat, isOperator } = useAuth();
  const { display, setDisplay } = useCurrency();
  const { rates, updatedAt, source, refresh: refreshFx, refreshing, canReadFx } = useFx();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("nf.sidebar.collapsed") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("nf.sidebar.collapsed", collapsed ? "1" : "0"); }
    catch {}
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/giris");
  };

  return (
    <div className="min-h-screen flex bg-[var(--nf-bg)]">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex bg-zinc-950 border-r border-zinc-900 flex-col fixed h-screen transition-[width] duration-300 ease-in-out ${collapsed ? "w-16" : "w-64"}`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <SidebarContent
          user={user}
          isAdmin={isAdmin}
          isMuhasebe={isMuhasebe}
          isSales={isSales}
          isKademe={isKademe}
          isSantiye={isSantiye}
          isSirketMuduru={isSirketMuduru}
          isBayi={isBayi}
          isSevkiyat={isSevkiyat}
          isOperator={isOperator}
          onLogout={handleLogout}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((v) => !v)}
        />
      </aside>

      {/* Mobile Drawer Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="p-0 w-72 bg-zinc-950 border-zinc-900 text-white flex flex-col [&>button]:text-zinc-400 [&>button]:hover:text-white"
          data-testid="mobile-sidebar"
        >
          <SidebarContent
            user={user}
            isAdmin={isAdmin}
            isMuhasebe={isMuhasebe}
            isSales={isSales}
            isKademe={isKademe}
            isSantiye={isSantiye}
            isSirketMuduru={isSirketMuduru}
            isBayi={isBayi}
            isSevkiyat={isSevkiyat}
            isOperator={isOperator}
            onLogout={handleLogout}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <div className={`flex-1 min-w-0 transition-[margin] duration-300 ease-in-out ${collapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        <header className="sticky top-0 z-10 bg-[var(--nf-bg)]/85 backdrop-blur border-b border-zinc-200">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16 gap-3">
            <div className="flex flex-1 items-center gap-2 min-w-0">
              {/* Hamburger — mobile only */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 shrink-0"
                data-testid="mobile-menu-btn"
                aria-label="Menüyü aç"
              >
                <Menu size={20} />
              </button>
              <div className="lg:hidden min-w-0 flex-1 md:flex-none">
                <CompanySwitcher compact />
              </div>
              <div className="hidden md:flex items-center gap-3">
                <span className="nf-label">Görüntüleme</span>
                <div className="flex items-center bg-white border border-zinc-200 rounded-md p-0.5" data-testid="currency-toggle">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c}
                      data-testid={`currency-${c}`}
                      onClick={() => setDisplay(c)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded ${
                        display === c ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                {canReadFx && <button
                  onClick={() => refreshFx(true)}
                  className="hidden xl:flex items-center gap-2 text-[11px] text-zinc-500 hover:text-zinc-900"
                  data-testid="fx-refresh-btn"
                  title={updatedAt ? `Son güncelleme: ${new Date(updatedAt).toLocaleString("tr-TR")}` : "Kur güncelle"}
                >
                  <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
                  <span className="nf-mono">1$ = ₺{Number(rates.USD).toFixed(2)}</span>
                  <span className="text-zinc-300">·</span>
                  <span className="nf-mono">1€ = ₺{Number(rates.EUR).toFixed(2)}</span>
                  <span className="uppercase tracking-widest text-[9px] text-zinc-400 pl-1">{source && source !== "fallback" ? "Canlı" : "Tahmini"}</span>
                </button>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-300 lg:hidden w-9 p-0 sm:w-auto sm:px-3"
                onClick={() => navigate("/mobil")}
                data-testid="header-mobile-btn"
                aria-label="Mobil Denetim"
                title="Mobil Denetim"
              >
                <Smartphone size={14} className="sm:mr-1.5" /><span className="hidden sm:inline">Mobil</span>
              </Button>
            </div>
          </div>
          {/* Mobile currency toggle — touch-friendly (min-h 40px) */}
          <div className="md:hidden px-4 pb-3 flex items-center gap-2 flex-wrap">
            <span className="nf-label shrink-0">Para Birimi</span>
            <div className="flex items-center bg-white border border-zinc-200 rounded-md p-0.5">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  data-testid={`currency-mobile-${c}`}
                  onClick={() => setDisplay(c)}
                  className={`px-3 py-2 min-h-[34px] text-[11px] font-semibold rounded ${
                    display === c ? "bg-zinc-900 text-white" : "text-zinc-600 active:bg-zinc-100"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            {canReadFx && <button
              onClick={() => refreshFx(true)}
              className="ml-auto flex items-center gap-1.5 text-[10px] text-zinc-500 hover:text-zinc-900 px-2 py-1.5 min-h-[34px] rounded-md border border-zinc-200 bg-white active:bg-zinc-50"
              data-testid="fx-refresh-btn-mobile"
              title="Kur güncelle"
            >
              <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
              <span className="nf-mono">1$=₺{Number(rates.USD).toFixed(2)}</span>
              <span className="text-zinc-300">·</span>
              <span className="nf-mono">1€=₺{Number(rates.EUR).toFixed(2)}</span>
            </button>}
          </div>
        </header>

        <main className="px-4 lg:px-8 py-6 lg:py-8">{children}</main>
        <footer className="border-t border-zinc-200 bg-white/50 mt-8" data-testid="app-footer">
          <div className="px-4 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-zinc-500">
            <div className="nf-mono uppercase tracking-wider">
              © {new Date().getFullYear()} Aladağ Grup — Tüm hakları saklıdır
            </div>
            <div className="text-zinc-400 text-center sm:text-right leading-relaxed">
              Bu uygulamadaki tüm içerik, veri ve tasarım Aladağ Grup'un fikri mülkiyetidir.
              İzinsiz çoğaltılamaz, dağıtılamaz veya üçüncü şahıslarla paylaşılamaz.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
